import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { http } from "@/lib/api";
import { ArrowLeft, ArrowRight, MagnifyingGlass, Camera } from "@phosphor-icons/react";

// PhotoPulse — full gallery page proxied from photopulse.pro (site=naturepulse).
// Public feed, no key required. Filters: type + free-text species. Paginated.

const TYPES = [
  { key: "", label: "All" },
  { key: "bird", label: "Birds" },
  { key: "mammal", label: "Mammals" },
  { key: "insect", label: "Insects" },
  { key: "plant", label: "Plants" },
  { key: "flower", label: "Flowers" },
  { key: "tree", label: "Trees" },
  { key: "fungi", label: "Fungi" },
  { key: "reptile", label: "Reptiles" },
  { key: "amphibian", label: "Amphibians" },
  { key: "fish", label: "Fish" },
  { key: "landscape", label: "Landscapes" },
];

const PAGE = 24;

export default function PhotoPulse() {
  const [photos, setPhotos] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [type, setType] = useState("");
  const [species, setSpecies] = useState("");
  const [speciesQuery, setSpeciesQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (type) params.set("type", type);
    if (species) params.set("species", species);
    http.get(`/photopulse/feed?${params.toString()}`)
      .then((r) => {
        if (!alive) return;
        setPhotos(r.data?.photos || []);
        setTotal(r.data?.total || 0);
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "PhotoPulse feed unavailable."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [offset, type, species]);

  const page = Math.floor(offset / PAGE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE));

  const onSearch = (e) => { e.preventDefault(); setOffset(0); setSpecies(speciesQuery.trim()); };
  const setTypeSafe = (k) => { setOffset(0); setType(k); };

  return (
    <main className="bg-[#F5EBDC] text-[#1B4332] pt-32 md:pt-36 pb-24 min-h-screen">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mb-10 md:mb-14">
        <div className="font-mono-tech text-[10px] md:text-xs tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          / PhotoPulse · NaturePulse curated
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
          The living gallery.
        </h1>
        <p className="mt-4 md:mt-6 max-w-2xl text-base md:text-lg text-[#3A3830] font-serif-display">
          Every photograph here was uploaded to NaturePulse and enriched by PhotoPulse&apos;s
          AI &mdash; species identified, geo-tagged, curated by human moderators. Powered by{" "}
          <a href="https://photopulse.pro" target="_blank" rel="noreferrer" className="underline decoration-[color:var(--np-brand,#B85F2A)]">
            photopulse.pro
          </a>.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mb-8">
        <div className="flex flex-wrap gap-1.5" data-testid="photopulse-filters">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setTypeSafe(t.key)}
              data-testid={`photopulse-type-${t.key || "all"}`}
              aria-pressed={type === t.key}
              className={`font-mono-tech text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border transition-colors ${
                type === t.key
                  ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
                  : "bg-transparent border-black/20 text-[#3A3830] hover:border-[color:var(--np-brand,#B85F2A)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={onSearch} className="mt-4 flex gap-2 max-w-md" data-testid="photopulse-search">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} weight="thin" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A3830]" />
            <input
              value={speciesQuery}
              onChange={(e) => setSpeciesQuery(e.target.value)}
              placeholder="Search species (e.g. heron, monarch)…"
              data-testid="photopulse-species-input"
              className="w-full bg-transparent border border-black/20 focus:border-[color:var(--np-brand,#B85F2A)] outline-none pl-9 pr-3 py-2 font-mono-tech text-xs tracking-[0.1em]"
            />
          </div>
          <button
            type="submit"
            data-testid="photopulse-species-submit"
            className="font-mono-tech text-[10px] tracking-[0.28em] uppercase px-4 py-2 border border-[#1B4332] hover:bg-[#1B4332] hover:text-[#F5EBDC] transition-colors"
          >
            Search
          </button>
          {species && (
            <button
              type="button"
              onClick={() => { setSpecies(""); setSpeciesQuery(""); setOffset(0); }}
              className="font-mono-tech text-[10px] tracking-[0.28em] uppercase px-3 py-2 text-[#3A3830] hover:text-[#1B4332]"
            >
              clear
            </button>
          )}
        </form>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        {error ? (
          <div data-testid="photopulse-error" className="border border-black/20 p-8 text-center">
            <div className="font-serif-display text-2xl mb-2">{error}</div>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#3A3830]">
              Try again in a moment.
            </div>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {Array.from({ length: PAGE }).map((_, i) => (
              <div key={i} className="aspect-square bg-black/5 animate-pulse" />
            ))}
          </div>
        ) : photos.length === 0 ? (
          <div data-testid="photopulse-empty" className="border border-dashed border-black/20 p-10 text-center">
            <Camera size={40} weight="thin" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-3" />
            <div className="font-serif-display text-2xl mb-1">No photos yet.</div>
            <div className="text-sm text-[#3A3830]">
              As NaturePulse contributors submit sightings, PhotoPulse enriches them and they appear here.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="photopulse-grid">
            {photos.map((p, i) => (
              <motion.a
                key={p.id || i}
                href={p.detail_url || p.image_url || "#"}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.02 }}
                data-testid={`photopulse-card-${p.id || i}`}
                className="group block relative overflow-hidden border border-black/10 aspect-square bg-black/5"
              >
                {p.image_url ? (
                  <img src={p.image_url} alt={p.title || p.species || "PhotoPulse photo"} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#3A3830]">
                    <Camera size={26} weight="thin" />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                  <div className="font-serif-display text-base leading-tight text-[#F5EBDC] line-clamp-2">
                    {p.title || p.species || "Untitled"}
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 text-[#E5D8C4]/85 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                    <span className="truncate">{
                      p.photographer
                        ? `by ${p.photographer}`
                        : (p.location?.display_name
                            || p.location?.name
                            || [p.location?.city, p.location?.state, p.location?.country].filter(Boolean).join(", ")
                            || (typeof p.location === "string" ? p.location : "")
                          )
                    }</span>
                    {p.species && <span className="italic normal-case tracking-[0.1em] text-[#E4A15C]">{p.species}</span>}
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </section>

      {/* Pagination */}
      {!loading && !error && photos.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-10 flex items-center justify-between">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#3A3830]">
            Page {page} of {pageCount} · {total.toLocaleString()} photos
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
              disabled={offset === 0}
              data-testid="photopulse-prev"
              className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase px-4 py-2 border border-black/20 disabled:opacity-40 hover:bg-[#1B4332] hover:text-[#F5EBDC] transition-colors"
            >
              <ArrowLeft size={12} weight="bold" /> Prev
            </button>
            <button
              onClick={() => setOffset(offset + PAGE)}
              disabled={page >= pageCount}
              data-testid="photopulse-next"
              className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase px-4 py-2 border border-black/20 disabled:opacity-40 hover:bg-[#1B4332] hover:text-[#F5EBDC] transition-colors"
            >
              Next <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </section>
      )}
    </main>
  );
}
